import React, { useMemo, useState } from "react";

import FormatCrypto from "@hl/base-components/lib/FormatCrypto";
import SwitchVertical01 from "@hl/base-components/lib/assets/icons.generated/HDS/Duotone icons/Arrows/SwitchVertical01";
import SwitchVertical02 from "@hl/base-components/lib/assets/icons.generated/HDS/Duotone icons/Arrows/SwitchVertical02";
import Cryptocurrency02 from "@hl/base-components/lib/assets/icons.generated/HDS/Duotone icons/Finance & eCommerce/Cryptocurrency02";
import Diamond01 from "@hl/base-components/lib/assets/icons.generated/HDS/Duotone icons/Finance & eCommerce/Diamond01";
import Hourglass01 from "@hl/base-components/lib/assets/icons.generated/HDS/Duotone icons/Time/Hourglass01";
import useMantineMediaQueries from "@hl/base-components/lib/hooks/useMantineMediaQueries";
import {
  TEXT_COLOR,
  UTILITY_COLOR,
} from "@hl/base-components/lib/theme/colors";
import {
  WEIGHT_BOLD,
  WEIGHT_NORMAL,
} from "@hl/base-components/lib/theme/typography";
import {
  NftContractStandard,
  OfferCriteriaKind,
} from "@hl/shared-features/lib/apollo/graphql.generated";
import { User } from "@hl/shared-features/lib/features/auth/User";
import { NoResults } from "@hl/shared-features/lib/features/collections/NoResults";
import { ResizedImage } from "@hl/shared-features/lib/features/image";
import MarketplaceFilterSortBar from "@hl/shared-features/lib/features/marketplace/components/MarketplaceFilterSortBar";
import MarketplacePrice from "@hl/shared-features/lib/features/marketplace/components/MarketplacePrice";
import OfferTypeFilter, {
  nonTokenOfferTypeOptions,
} from "@hl/shared-features/lib/features/marketplace/components/OfferTypeFilter";
import { advancedInputStyles } from "@hl/shared-features/lib/features/marketplace/drawers/ListingDrawer";
import AcceptBidModalHighlight, {
  AcceptBidModalStep,
  AcceptBidModalTitle,
} from "@hl/shared-features/lib/features/marketplace/modals/AcceptBidModal";
import BidModalHighlight from "@hl/shared-features/lib/features/marketplace/modals/BidModal";
import CancelBidModalHighlight from "@hl/shared-features/lib/features/marketplace/modals/CancelBidModal";
import { useGetCollectionOffersQuery } from "@hl/shared-features/lib/features/marketplace/queries/marketplace.graphql.generated";
import {
  CollectionOffer,
  MarketplaceCollection,
} from "@hl/shared-features/lib/features/marketplace/utils";
import { useModalStack } from "@hl/shared-features/lib/features/modal";
import usePercentDifference from "@hl/shared-features/lib/hooks/usePercentDifference";
import { SortingOption } from "@hl/shared-features/lib/utils/sortingOptions";
import {
  Box,
  Button,
  Center,
  Group,
  Loader,
  Stack,
  Switch,
  Table,
  Text,
  useMantineTheme,
} from "@mantine/core";
import dayjs, { extend } from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import relativeTime from "dayjs/plugin/relativeTime";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import InfiniteScroll from "react-infinite-scroll-component";

import useMintState from "~hooks/useMintState";

import {
  OrderSortOptions,
  SortDirection,
} from "../../../apollo/graphql.generated";

extend(relativeTime);
extend(advancedFormat);
extend(utc);
extend(timezone);

const sortingOptions: SortingOption<OrderSortOptions>[] = [
  {
    value: "price_desc",
    label: "Highest price",
    sortBy: OrderSortOptions.PRICE,
    sortDirection: SortDirection.DESC,
    icon: SwitchVertical01,
  },
  {
    value: "price_asc",
    label: "Lowest price",
    sortBy: OrderSortOptions.PRICE,
    sortDirection: SortDirection.ASC,
    icon: SwitchVertical02,
  },
  {
    value: "exp_asc",
    label: "Expiring soonest",
    sortBy: OrderSortOptions.EXPIRING,
    sortDirection: SortDirection.ASC,
    icon: Cryptocurrency02,
  },
  {
    value: "date_desc",
    label: "Newest first",
    sortBy: OrderSortOptions.EXPIRING,
    sortDirection: SortDirection.DESC,
    icon: Hourglass01,
  },
  {
    value: "highest_above_floor",
    label: "Highest above floor",
    sortBy: OrderSortOptions.HIGHEST_ABOVE_FLOOR,
    sortDirection: SortDirection.DESC,
    icon: Diamond01,
  },
];

const MarketplaceOffersList = () => {
  const theme = useMantineTheme();
  const [searchString, setSearchString] = useState<string>("");
  const [sorting, setSorting] = useState<string | null>(
    sortingOptions[0].value
  );
  const { pushModal } = useModalStack();
  const { collection, enableCollectionOffer, chainId, address } =
    useMintState();

  const [criteriaKind, setCriteriaType] = useState<OfferCriteriaKind>(
    nonTokenOfferTypeOptions[0].value
  );
  const { isSmall, isExtraSmall, isLarge, isMobile } = useMantineMediaQueries({
    withInitialValues: true,
  });
  const [aboveFloor, setAboveFloor] = useState<boolean>(false);
  const selectedSort = useMemo(
    () =>
      sortingOptions.find((sortingOption) => sortingOption.value === sorting),
    [sorting]
  );
  const marketplaceId = collection?.marketplaceId ?? "";
  const collectionId = `${chainId}:${address}:${
    collection?.editionId ?? collection?.entityId ?? 0
  }`;

  const { data, fetchMore, loading, error, refetch } =
    useGetCollectionOffersQuery({
      variables: {
        createdAfter: null,
        aboveFloor: aboveFloor || null,
        sortDirection: selectedSort!.sortDirection,
        sortBy: selectedSort!.sortBy,
        after: null,
        first: 10,
        collectionId,
        criteriaKind: criteriaKind || null,
      },
      skip: !marketplaceId,
    });

  const bids = useMemo(
    () => data?.getCollectionOffers?.edges || [],
    [data?.getCollectionOffers?.edges]
  );

  const nextCursor = data?.getCollectionOffers?.pageInfo.endCursor;
  const hasNextPage = data?.getCollectionOffers?.pageInfo?.hasNextPage;

  const tableHeaders = (
    <tr>
      <th>Offer Price</th>
      <th>Item</th>
      <th>Expires In</th>
      <th>From</th>
      <th></th>
    </tr>
  );

  const tableRows = bids.map((bid, i) => (
    <ReceivedOfferRow bid={bid} key={i} refetch={refetch} />
  ));

  const aboveFloorFilter = (
    <Group
      h={40}
      px={16}
      w={isExtraSmall ? "100%" : undefined}
      position="apart"
      align="center"
      noWrap
      sx={advancedInputStyles}
    >
      <Text size="sm" fw={WEIGHT_BOLD} sx={{ whiteSpace: "nowrap" }}>
        Above floor
      </Text>
      <Switch
        display="flex"
        checked={aboveFloor}
        onChange={(e) => setAboveFloor(e.currentTarget.checked)}
      />
    </Group>
  );

  const typeFilter = (
    <OfferTypeFilter
      offerType={criteriaKind}
      setOfferType={setCriteriaType}
      options={nonTokenOfferTypeOptions}
    />
  );

  const customViewElement = (
    <Group sx={{ flexWrap: isSmall ? "nowrap" : "wrap" }}>
      {typeFilter}
      {isLarge && aboveFloorFilter}
    </Group>
  );

  const makeOfferButton =
    !!collection && enableCollectionOffer ? (
      <Button
        size="lg"
        w={isMobile ? "100%" : undefined}
        onClick={() =>
          pushModal(
            <BidModalHighlight
              src={collection?.collectionImage || ""}
              collectionData={collection}
              onCompleted={() => setTimeout(() => refetch(), 3000)}
            />,
            {
              size: "lg",
              title: (
                <Text size="md" fw={WEIGHT_BOLD}>
                  Make offer
                </Text>
              ),
            }
          )
        }
      >
        Make offer
      </Button>
    ) : (
      <></>
    );

  return (
    <Stack>
      <MarketplaceFilterSortBar
        setSorting={setSorting}
        sorting={sorting}
        customFilterElement={!isLarge ? aboveFloorFilter : undefined}
        hasFilterToggle={!isLarge}
        customViewElement={customViewElement}
        sortingOptions={sortingOptions}
        setSearchString={setSearchString}
        searchString={searchString}
        customLeftSection={makeOfferButton}
        searchPlaceholder="Filter offers by collection name"
      />
      {loading ? (
        <Loading />
      ) : bids.length ? (
        <InfiniteScroll
          dataLength={bids.length}
          next={() =>
            hasNextPage &&
            fetchMore({
              variables: { after: nextCursor },
              updateQuery: (previousResult, { fetchMoreResult }) => {
                fetchMoreResult!.getCollectionOffers.edges = [
                  ...(previousResult?.getCollectionOffers?.edges ?? []),
                  ...(fetchMoreResult?.getCollectionOffers?.edges ?? []),
                ];
                return fetchMoreResult;
              },
            })
          }
          hasMore={!!hasNextPage}
          loader={<Loading />}
        >
          <Table
            verticalSpacing="md"
            miw={1200}
            sx={{
              "thead tr th": {
                color: theme.colors.secondaryText[0],
                paddingLeft: 0,
                fontSize: 14,
                fontWeight: WEIGHT_NORMAL,
                paddingTop: 8,
              },
              "tbody tr td": {
                paddingLeft: 0,
              },
              "tbody tr td:last-child": {
                paddingRight: 0,
              },
            }}
          >
            <thead>{tableHeaders}</thead>
            <tbody>{tableRows}</tbody>
          </Table>
        </InfiniteScroll>
      ) : !!error ? (
        <NoResults
          title="Something went wrong"
          description="Please try again later"
        />
      ) : (
        <NoResults
          description="Once you receive any, they'll appear here."
          title="No offers received yet"
        />
      )}
    </Stack>
  );
};

function ReceivedOfferRow({
  bid,
  refetch,
}: {
  bid: CollectionOffer;
  refetch: () => void;
}) {
  const { pushModal } = useModalStack();
  const {
    price,
    floorPrice,
    tokens,
    quantityRemaining,
    chainId,
    createdAt,
    criteriaKind,
    expiredAt,
    user,
    kind,
    makerAddress,
    attributeValue,
    attributeKey,
  } = bid;
  const { diffWording } = usePercentDifference(
    price?.amount?.native,
    floorPrice,
    true
  );
  const { collection, walletAddress, marketplaceId } = useMintState();
  const token = tokens?.[0];
  const tokenId = token?.id ?? "";

  const collectionData: MarketplaceCollection = {
    name: collection?.name ?? "",
    address: collection?.address ?? "",
    chainId: chainId,
    id: collection?.id,
    onchainId: collection?.onchainId ?? null,
    marketplaceId: marketplaceId ?? null,
    imageUrl: collection?.collectionImage ?? "",
    editionId: collection?.entityId ?? collection?.editionId,
    collectionType: collection?.collectionType,
    standard:
      kind === "erc1155"
        ? NftContractStandard.ERC1155
        : NftContractStandard.ERC721,
  };

  const src = token?.imageUrl ?? collection?.collectionImage ?? "";
  const fp = floorPrice && floorPrice != "0" ? parseFloat(floorPrice) : null;

  return (
    <tr key={createdAt}>
      <td>
        <MarketplacePrice price={price} />
        {fp && (
          <Text size="sm" color={TEXT_COLOR.SECONDARY}>
            <FormatCrypto
              amount={fp}
              onlyText
              symbol={bid.price.currency.symbol}
            />{" "}
            floor ({diffWording})
          </Text>
        )}
      </td>
      <td>
        <Group noWrap>
          <Box p={8} bg={UTILITY_COLOR.TABLE_ROW_BACKGROUND}>
            <ResizedImage src={src} width={64} height={64} fit="contain" />
          </Box>
          <Stack spacing={0} lh={1.4}>
            <Group spacing="xs">
              <Text fw={WEIGHT_BOLD}>
                {criteriaKind === "token" && token?.name}
                {criteriaKind === "attribute" &&
                  `Trait: ${attributeKey} ${attributeValue}`}
                {criteriaKind === "collection" && "Collection offer"}
              </Text>
              {quantityRemaining > 1 && <Text>x{quantityRemaining}</Text>}
            </Group>
            <Text size="sm">{collection?.name}</Text>
          </Stack>
        </Group>
      </td>
      <td>
        {expiredAt && (
          <>
            <Text>
              {dayjs().isAfter(expiredAt) ? "Expired" : dayjs().to(expiredAt)}
            </Text>
            <Text size="sm" color={TEXT_COLOR.SECONDARY}>
              {dayjs(expiredAt).format("MMM. DD @ h:mma z")}
            </Text>
          </>
        )}
      </td>
      <td>
        <User
          enableLink={!!user?.id}
          textSize="md"
          walletAddresses={[makerAddress]}
        />
      </td>
      <td align="right">
        {!dayjs().isAfter(expiredAt) && (
          <>
            {!!walletAddress &&
              walletAddress?.toLowerCase() !== makerAddress.toLowerCase() && (
                <Button
                  size="lg"
                  px={12}
                  w="100%"
                  maw="160px"
                  disabled={dayjs().isAfter(expiredAt)}
                  onClick={() =>
                    pushModal(
                      <AcceptBidModalHighlight
                        bid={bid}
                        collectionData={collectionData}
                        tokenId={tokenId}
                        step={
                          criteriaKind === "token"
                            ? AcceptBidModalStep.Default
                            : AcceptBidModalStep.SelectToken
                        }
                        onCompleted={() => setTimeout(() => refetch(), 3000)}
                      />,
                      {
                        size: "lg",
                        title: (
                          <AcceptBidModalTitle
                            address={makerAddress}
                            isCollection={criteriaKind !== "token"}
                          />
                        ),
                      }
                    )
                  }
                >
                  View offer
                </Button>
              )}
            {walletAddress?.toLowerCase() === makerAddress.toLowerCase() && (
              <Button
                w="100%"
                size="lg"
                px={12}
                maw="160px"
                onClick={() =>
                  pushModal(
                    <CancelBidModalHighlight
                      collectionData={collection!}
                      imageUrl={collection?.collectionImage ?? ""}
                      id={bid.reservoirId}
                      floorPrice={bid.floorPrice ?? undefined}
                      tokenId={token?.id}
                      onCancel={() => setTimeout(() => refetch(), 3000)}
                    />,
                    {
                      size: "lg",
                      title: <Text fw={WEIGHT_BOLD}>Cancel your offer</Text>,
                    }
                  )
                }
              >
                Manage offer
              </Button>
            )}
          </>
        )}
      </td>
    </tr>
  );
}

const Loading = () => (
  <Center p="lg" w="100%">
    <Loader />
  </Center>
);

export default MarketplaceOffersList;
